import React from 'react';
import ProgressHeader from './ProgressHeader';
import ContinueButton from './common/ContinueButton';
import { GENDERS, BIRTH_YEARS } from '../constants/onboardingData';
import { CircleCheck } from 'lucide-react';

const STUDENT_DIFFICULTIES = [
  { id: 'vision', label: 'Vision', description: 'Je vois mal le tableau', emoji: '👓' },
  { id: 'audition', label: 'Audition', description: "J'entends mal", emoji: '👂' },
  { id: 'concentration', label: 'Concentration', description: "J'ai du mal à me concentrer", emoji: '🧠' },
  { id: 'aucune', label: 'Aucune', description: 'Tout va bien', emoji: '✅' },
];

/**
 * Step 3 (Flow Élève): Infos personnelles (genre, année naissance, difficultés)
 */
export default function Step3StudentInfo({
  formData,
  currentStepNumber,
  currentStepMessage,
  totalSteps,
  canContinue,
  validationMessage,
  onUpdateStudentInfo,
  onToggleStudentDifficulty,
  onContinue,
  onBack,
}) {
  return (
    <div className="min-h-[100dvh] bg-[#F0FDFA] flex flex-col">
      <ProgressHeader
        currentStep={currentStepNumber}
        totalSteps={totalSteps}
        message={currentStepMessage}
        showBack={true}
        onBack={onBack}
      />

      <div className="flex-1 px-4 pb-4 overflow-y-auto">
        {/* Titre */}
        <div className="text-center py-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Encore quelques infos, {formData.studentName}
          </h1>
          <p className="text-gray-500">
            Ça aide GeniMi à mieux t'accompagner
          </p>
        </div>

        {/* Question 1: Genre */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-[#E0F2FE] text-[#0284C7] px-2 py-1 rounded text-sm font-bold">1</span>
            <span className="font-bold text-gray-800">Tu es ?</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {GENDERS.map((gender) => (
              <div
                key={gender.id}
                onClick={() => onUpdateStudentInfo('studentGender', gender.id)}
                className={`
                  p-4 rounded-xl cursor-pointer transition-all text-center
                  ${formData.studentGender === gender.id
                    ? 'border-[#38BDF8] bg-[#E0F7FA] border-2'
                    : 'border-gray-200 hover:border-gray-300 border-2'
                  }
                `}
              >
                <span className="text-3xl block mb-1">
                  {gender.id === 'garcon' ? '👦🏾' : '👧🏾'}
                </span>
                <span className="font-medium text-gray-700">
                  {gender.id === 'garcon' ? 'Un garçon' : 'Une fille'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Question 2: Année de naissance */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-[#E0F2FE] text-[#0284C7] px-2 py-1 rounded text-sm font-bold">2</span>
            <span className="font-bold text-gray-800">Ton année de naissance</span>
          </div>

          <select
            value={formData.studentBirthYear || ''}
            onChange={(e) => onUpdateStudentInfo('studentBirthYear', parseInt(e.target.value))}
            className="w-full p-3 border-2 border-gray-200 rounded-xl text-gray-700 
                       focus:border-[#38BDF8] focus:outline-none transition-colors"
          >
            <option value="">Sélectionne une année</option>
            {BIRTH_YEARS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Question 3: Difficultés */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-[#E0F2FE] text-[#0284C7] px-2 py-1 rounded text-sm font-bold">3</span>
            <span className="font-bold text-gray-800">As-tu des difficultés ?</span>
          </div>
          <p className="text-gray-500 text-sm mb-4 ml-8">
            Tu peux choisir plusieurs réponses
          </p>

          <div className="space-y-3">
            {STUDENT_DIFFICULTIES.map((difficulty) => (
              <div
                key={difficulty.id}
                onClick={() => onToggleStudentDifficulty(difficulty.id)}
                className={`
                  p-4 rounded-xl cursor-pointer transition-all flex items-center gap-4 relative
                  ${formData.studentDifficulties?.includes(difficulty.id)
                    ? 'border-[#10B981] bg-[#ECFDF5] border-2'
                    : 'border-gray-200 hover:border-gray-300 border-2'
                  }
                `}
              >
                <span className="text-2xl">{difficulty.emoji}</span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{difficulty.label}</p>
                  <p className="text-gray-500 text-sm">{difficulty.description}</p>
                </div>
                {formData.studentDifficulties?.includes(difficulty.id) && (
                  <CircleCheck className="text-white" size={28} fill="#10B981" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bouton Continuer */}
      <div className="sticky bottom-0 bg-[#F0FDFA] p-4 border-t border-gray-100">
        {validationMessage && !canContinue && (
          <p className="text-center text-sm text-gray-500 mb-2">{validationMessage}</p>
        )}
        <ContinueButton
          onClick={onContinue}
          disabled={!canContinue}
        />
      </div>
    </div>
  );
}
